import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, RefreshCw, Inbox, LogOut } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleMark = () => (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
        <path
            fill="#4285F4"
            d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z"
        />
        <path
            fill="#34A853"
            d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z"
        />
        <path
            fill="#FBBC05"
            d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z"
        />
        <path
            fill="#EA4335"
            d="M12 4.77c1.76 0 3.34.61 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.88 8.87 4.77 12 4.77z"
        />
    </svg>
);

const Studio = () => {
    const location = useLocation();
    const [key, setKey] = useState(
        () => localStorage.getItem("cs_studio_key") || ""
    );
    const [user, setUser] = useState(location.state?.user || null);
    const [checking, setChecking] = useState(true);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [avail, setAvail] = useState({ enabled: true, next_slot: "", slots: 1 });

    const signInWithGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/studio";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const authConfig = (k) => ({
        withCredentials: true,
        headers: k ? { "X-Studio-Key": k } : {},
    });

    const load = async (k) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/studio/overview`, authConfig(k));
            setData(res.data);
            setAvail(res.data.availability);
            if (k) localStorage.setItem("cs_studio_key", k);
            return true;
        } catch (err) {
            if (k) localStorage.removeItem("cs_studio_key");
            return false;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const init = async () => {
            if (location.state?.user) {
                await load(null);
                setChecking(false);
                return;
            }
            try {
                const res = await axios.get(`${API}/auth/me`, {
                    withCredentials: true,
                });
                setUser(res.data);
                await load(null);
            } catch (err) {
                if (key) {
                    const ok = await load(key);
                    if (!ok) setKey("");
                }
            }
            setChecking(false);
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(
                `${API}/studio/availability`,
                avail,
                authConfig(data && !key ? null : key || null)
            );
            toast.success("Availability updated. The banner is live.");
        } catch (err) {
            toast.error("Could not save. Please sign in again.");
        } finally {
            setSaving(false);
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch (err) {}
        localStorage.removeItem("cs_studio_key");
        setUser(null);
        setData(null);
        setKey("");
    };

    const inputClass =
        "w-full rounded-xl border border-slate-200 bg-mist px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-brand focus:bg-white focus:ring-2 focus:ring-brand/15";

    if (checking) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-mist">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-mist px-4">
                <div
                    data-testid="studio-login"
                    className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8"
                >
                    <img
                        src="/logo-mark.png"
                        alt="CS Studio logo"
                        className="h-11 w-11 object-contain"
                    />
                    <h1 className="mt-5 text-xl font-bold tracking-tight text-ink">
                        Studio panel
                    </h1>
                    <p className="mt-2 text-sm text-slate-500">
                        Sign in with your Google account to manage availability
                        and view enquiries.
                    </p>
                    <button
                        type="button"
                        data-testid="studio-google-signin"
                        onClick={signInWithGoogle}
                        className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-ink transition-colors duration-300 hover:border-brand hover:bg-ice"
                    >
                        <GoogleMark />
                        Continue with Google
                    </button>
                    <div className="my-5 flex items-center gap-3">
                        <span className="h-px flex-1 bg-slate-200" />
                        <span className="text-[11px] font-medium uppercase tracking-widest text-slate-400">
                            or use studio key
                        </span>
                        <span className="h-px flex-1 bg-slate-200" />
                    </div>
                    <input
                        type="password"
                        data-testid="studio-key-input"
                        value={key}
                        onChange={(e) => setKey(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load(key)}
                        placeholder="Studio key"
                        className={inputClass}
                    />
                    <button
                        type="button"
                        data-testid="studio-login-button"
                        onClick={async () => {
                            const ok = await load(key);
                            if (!ok) toast.error("Wrong studio key. Please try again.");
                        }}
                        disabled={loading}
                        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-dark disabled:opacity-60"
                    >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Open with key"}
                    </button>
                    <a
                        href="/"
                        data-testid="studio-back-link"
                        className="mt-5 flex items-center justify-center gap-1.5 text-xs font-medium text-slate-400 transition-colors duration-200 hover:text-brand-dark"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Back to site
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-mist" data-testid="studio-panel">
            <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <img
                            src="/logo-mark.png"
                            alt="CS Studio logo"
                            className="h-9 w-9 object-contain"
                        />
                        <h1 className="text-lg font-bold tracking-tight text-ink">
                            Studio panel
                        </h1>
                    </div>
                    <div className="flex items-center gap-4">
                        {user && (
                            <span
                                data-testid="studio-user-chip"
                                className="hidden items-center gap-2 sm:flex"
                            >
                                {user.picture && (
                                    <img
                                        src={user.picture}
                                        alt=""
                                        className="h-7 w-7 rounded-full border border-slate-200"
                                    />
                                )}
                                <span className="text-xs font-medium text-slate-500">
                                    {user.name || user.email}
                                </span>
                            </span>
                        )}
                        <button
                            type="button"
                            data-testid="studio-logout-button"
                            onClick={logout}
                            className="flex items-center gap-1.5 text-xs font-medium text-slate-400 transition-colors duration-200 hover:text-brand-dark"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Sign out
                        </button>
                        <a
                            href="/"
                            data-testid="studio-back-link"
                            className="flex items-center gap-1.5 text-xs font-medium text-slate-400 transition-colors duration-200 hover:text-brand-dark"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
                        </a>
                    </div>
                </div>

                <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
                    <h2 className="text-base font-semibold text-ink">
                        Availability banner
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">
                        Controls the "Now booking" strip at the top of your site.
                    </p>
                    <label
                        className="mt-5 flex cursor-pointer items-center gap-3"
                        data-testid="studio-availability-toggle"
                    >
                        <input
                            type="checkbox"
                            checked={avail.enabled}
                            onChange={(e) =>
                                setAvail((a) => ({ ...a, enabled: e.target.checked }))
                            }
                            className="h-4 w-4 accent-brand"
                        />
                        <span className="text-sm font-medium text-ink">
                            Show the banner on the site
                        </span>
                    </label>
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <label className="mb-2 block text-xs font-semibold text-slate-600">
                                Next slot opens
                            </label>
                            <input
                                type="text"
                                data-testid="studio-next-slot-input"
                                value={avail.next_slot}
                                onChange={(e) =>
                                    setAvail((a) => ({ ...a, next_slot: e.target.value }))
                                }
                                placeholder="e.g. October 2026"
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-xs font-semibold text-slate-600">
                                Slots available
                            </label>
                            <input
                                type="number"
                                min="0"
                                max="50"
                                data-testid="studio-slots-input"
                                value={avail.slots}
                                onChange={(e) =>
                                    setAvail((a) => ({
                                        ...a,
                                        slots: parseInt(e.target.value || "0", 10),
                                    }))
                                }
                                className={inputClass}
                            />
                        </div>
                    </div>
                    <button
                        type="button"
                        data-testid="studio-save-button"
                        onClick={save}
                        disabled={saving}
                        className="mt-5 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-dark disabled:opacity-60"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
                    </button>
                </div>

                <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
                    <div className="flex items-center justify-between">
                        <h2 className="flex items-center gap-2 text-base font-semibold text-ink">
                            <Inbox className="h-4 w-4 text-brand-dark" />
                            Enquiries ({data.enquiries.length})
                        </h2>
                        <button
                            type="button"
                            data-testid="studio-refresh-button"
                            onClick={() => load(key || null)}
                            className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 transition-colors duration-200 hover:bg-ice"
                        >
                            <RefreshCw className="h-3.5 w-3.5" /> Refresh
                        </button>
                    </div>
                    <div className="mt-5 space-y-4">
                        {data.enquiries.length === 0 && (
                            <p className="text-sm text-slate-400">No enquiries yet.</p>
                        )}
                        {data.enquiries.map((e) => (
                            <div
                                key={e.id}
                                data-testid={`studio-enquiry-${e.id}`}
                                className="rounded-xl border border-slate-100 bg-mist p-5"
                            >
                                <div className="flex flex-wrap items-baseline justify-between gap-2">
                                    <p className="text-sm font-semibold text-ink">
                                        {e.name}
                                        {e.business_name && (
                                            <span className="font-normal text-slate-500">
                                                {" "}
                                                · {e.business_name}
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-xs text-slate-400">
                                        {new Date(e.created_at).toLocaleString()}
                                    </p>
                                </div>
                                <p className="mt-1 text-xs font-medium text-brand-deep">
                                    {e.service} · {e.budget}
                                </p>
                                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                                    {e.message}
                                </p>
                                <a
                                    href={`mailto:${e.email}`}
                                    data-testid={`studio-enquiry-reply-${e.id}`}
                                    className="mt-3 inline-block text-xs font-semibold text-ink underline decoration-brand/50 underline-offset-2 transition-colors duration-200 hover:text-brand-dark"
                                >
                                    Reply to {e.email}
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Studio;
